// render-my-adventures.ts — the "catalog-my-adventures" renderer: the creator's own adventures
// dashboard (my_adventures.php).
//
// PHP selects raw rows; the browser builds and filters cards in memory. The card is the
// rich MY-ADVENTURE management card: a 10:13 cover image or hover-play header video, level/price/release
// footer, AND a dense interactive management cluster (locations/encounters, edit/private/delete,
// media manager). The cover is the view link.
//
// TypeScript owns the card shell and action buttons; PHP ships a pure-data JSON payload.
//
// The toolbar search box (data-ft-search-input-id) filters cards by name, instantly and client-side —
// replacing the page's old inline name-filter <script>.

import type { CatalogListConfig, CatalogMyAdventuresResponse, MyAdventureCard } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';
import { adventureMyCard, validateAdventureList } from './adventure-list-data';

const CLAMP_2 = 'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;';

function icon(id: string, w = 20, h = 20, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function appendIconButton(
    parent: HTMLElement,
    href: string,
    id: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    title: string | boolean = false,
): void {
    const a = document.createElement('a');
    a.href = href;
    a.id = id;
    a.className = className;
    a.setAttribute('aria-label', ariaLabel);
    a.innerHTML = icon(iconId, 20, 20, title);
    parent.appendChild(a);
}

function appendActionButton(
    parent: HTMLElement,
    id: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    title: string | boolean,
    display: string,
    onClick: () => void | Promise<void>,
): HTMLButtonElement {
    const button = document.createElement('button');
    button.id = id;
    button.type = 'button';
    button.className = className;
    button.style.display = display;
    button.setAttribute('aria-label', ariaLabel);
    button.innerHTML = icon(iconId, 20, 20, title);
    button.addEventListener('click', () => { void onClick(); });
    parent.appendChild(button);
    return button;
}

function renderAdventureManagementActions(parent: HTMLElement, card: MyAdventureCard): void {
    const adventureId = card.item_id;
    const publicId = `adventure_public_${adventureId}`;
    const privateId = `adventure_private_${adventureId}`;
    const deleteId = `adventure_delete_${adventureId}`;
    const confirmId = `adventure_confirm_${adventureId}`;
    const privacyUrl = resolveUrl('options/db_queries/update/update_privacy_inc.php');
    const deleteUrl = resolveUrl('options/db_queries/delete/soft_delete_inc.php');

    const publicButton = appendActionButton(
        parent,
        publicId,
        'btn btn-primary btn-sm',
        'public',
        'Make private',
        'Public',
        card.is_private ? 'none' : 'block',
        async () => {
            await queryData(privacyUrl, 'div_sidelog_warning', {
                item_type: 'adventure',
                adventure_id: adventureId,
                adventure_private: '1',
            });
            publicButton.style.display = 'none';
            privateButton.style.display = 'block';
        },
    );

    const privateButton = appendActionButton(
        parent,
        privateId,
        'btn btn-danger btn-sm',
        'private',
        'Make public',
        'Private',
        card.is_private ? 'block' : 'none',
        async () => {
            await queryData(privacyUrl, 'div_sidelog_warning', {
                item_type: 'adventure',
                adventure_id: adventureId,
                adventure_private: '0',
            });
            privateButton.style.display = 'none';
            publicButton.style.display = 'block';
        },
    );

    const deleteButton = appendActionButton(
        parent,
        deleteId,
        'btn btn-warning btn-sm',
        'delete',
        'Delete adventure',
        'Delete',
        'block',
        () => {
            deleteButton.style.display = 'none';
            confirmButton.style.display = 'block';
        },
    );

    const confirmButton = appendActionButton(
        parent,
        confirmId,
        'btn btn-danger btn-sm',
        'confirm',
        'Confirm delete',
        'Confirm',
        'none',
        async () => {
            await queryData(deleteUrl, `div_refresh_adventure_card_${adventureId}`, {
                item_type: 'adventure',
                adventure_id: adventureId,
            });
        },
    );
}

function renderMyAdventureCard(card: MyAdventureCard): HTMLElement {
    const col = document.createElement('div');
    col.className = 'col-12 col-md-6 col-lg-4 col-xl-3 p-2 js-my-adventure-card';
    col.dataset.adventureName = card.name || '';

    const refresh = document.createElement('div');
    refresh.id = `div_refresh_adventure_card_${card.item_id}`;
    refresh.className = 'h-100 d-flex flex-column';
    col.appendChild(refresh);

    const shell = document.createElement('div');
    shell.className = 'card text-left w-100 h-100 shadow-sm border border-secondary border-opacity-25 d-flex flex-column'
        + (card.video_url ? ' video-card-hover' : '');
    shell.setAttribute('data-bs-theme', 'dark');
    refresh.appendChild(shell);

    const link = document.createElement('a');
    link.href = card.url;
    link.className = 'text-decoration-none flex-shrink-0';
    shell.appendChild(link);

    const cover = document.createElement('div');
    cover.className = 'overflow-hidden w-100';
    cover.setAttribute('style', 'aspect-ratio: 10/13;');
    link.appendChild(cover);

    if (card.video_url) {
        const video = document.createElement('video');
        video.className = 'w-100 h-100';
        video.style.objectFit = 'cover';
        video.style.objectPosition = 'top';
        video.src = card.video_url;
        video.muted = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'metadata');
        if (card.picture_url) video.setAttribute('poster', card.picture_url);
        cover.appendChild(video);
    } else {
        const img = document.createElement('img');
        img.src = card.picture_url || '';
        img.className = 'w-100 h-100';
        img.alt = card.name || 'Adventure';
        img.style.objectFit = 'cover';
        img.style.objectPosition = 'top';
        img.loading = 'lazy';
        img.decoding = 'async';
        cover.appendChild(img);
    }

    const body = document.createElement('div');
    body.className = 'card-body d-flex flex-column flex-grow-1 py-2 px-3';
    shell.appendChild(body);

    const title = document.createElement('h6');
    title.className = 'card-title small fw-semibold mb-2 text-white text-break lh-sm';
    title.setAttribute('style', CLAMP_2);
    title.textContent = card.name || 'Untitled';
    body.appendChild(title);

    const badges = document.createElement('div');
    badges.className = 'd-flex flex-wrap align-items-center gap-1 mb-2';
    const status = document.createElement('span');
    status.className = `badge rounded-pill ${card.is_private ? 'bg-danger-subtle text-danger-emphasis' : 'bg-primary-subtle text-primary-emphasis'}`;
    status.textContent = card.is_private ? 'Private' : 'Public';
    badges.appendChild(status);

    // Nested adventures: a MASTER (bundle) card is flagged as such and gets a clickable chip that
    // drills into its chapters (my_adventures.php?parent_id=…). Leaves (child_count 0) show neither.
    if (card.child_count && card.child_count > 0) {
        const bundleBadge = document.createElement('span');
        bundleBadge.className = 'badge rounded-pill bg-secondary-subtle text-secondary-emphasis';
        bundleBadge.textContent = 'Bundle';
        badges.appendChild(bundleBadge);

        if (card.children_url) {
            const subLink = document.createElement('a');
            subLink.href = card.children_url;
            subLink.className = 'badge rounded-pill bg-info-subtle text-info-emphasis text-decoration-none';
            subLink.setAttribute('aria-label', `View ${card.child_count} sub-adventures`);
            subLink.textContent = `${card.child_count} sub-adventure${card.child_count === 1 ? '' : 's'} →`;
            badges.appendChild(subLink);
        }
    }
    body.appendChild(badges);

    const meta = document.createElement('div');
    meta.className = 'mt-auto d-flex flex-column gap-1 small';
    body.appendChild(meta);

    const level = document.createElement('div');
    level.className = 'd-flex align-items-center gap-1 text-white-50 lh-sm';
    level.innerHTML = icon('level') + ' ';
    const begin = document.createElement('span');
    begin.className = 'badge bg-primary';
    begin.textContent = card.level_begin || '0';
    level.appendChild(begin);
    level.appendChild(document.createTextNode(' to '));
    const end = document.createElement('span');
    end.className = 'badge bg-secondary';
    end.textContent = card.level_end || '0';
    level.appendChild(end);
    meta.appendChild(level);

    const footer = document.createElement('div');
    footer.className = 'card-footer ft-adventure-catalog-card__footer flex-shrink-0 d-flex align-items-center justify-content-between gap-2 px-3 py-2 border-top border-0 border-opacity-25';
    shell.appendChild(footer);

    const price = document.createElement('span');
    price.className = 'ft-adventure-catalog-card__price fw-semibold lh-sm';
    const priceInner = document.createElement('span');
    priceInner.className = card.is_free ? 'text-success' : 'text-warning';
    priceInner.textContent = card.is_free ? 'Free' : `$${card.price_f || '0.00'}`;
    price.appendChild(priceInner);
    footer.appendChild(price);

    if (card.release_date) {
        const release = document.createElement('span');
        release.className = 'ft-adventure-catalog-card__release text-muted small lh-sm text-end';
        release.textContent = card.release_date;
        footer.appendChild(release);
    }

    const primaryActions = document.createElement('div');
    primaryActions.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2';
    const primaryGroup = document.createElement('div');
    primaryGroup.className = 'd-flex flex-wrap align-items-center gap-1';
    appendIconButton(primaryGroup, card.edit_url, `btn_edit_adventure_${card.item_id}`, 'btn btn-sm btn-primary', 'edit_adventure', 'Edit adventure');
    appendIconButton(primaryGroup, card.locations_url, `btn_locations_${card.item_id}`, 'btn btn-sm btn-outline-secondary rounded border border-secondary border-opacity-75', 'locations', 'My locations');
    appendIconButton(primaryGroup, card.encounters_url, `btn_encounters_${card.item_id}`, 'btn btn-sm btn-outline-secondary rounded border border-secondary border-opacity-75', 'adventure_encounters', 'My encounters');
    appendIconButton(primaryGroup, card.media_url, `btn_media_manager_${card.item_id}`, 'btn btn-sm btn-outline-info', 'modify_picture_gallery', 'Media manager', 'Media Manager - header galleries + image/video upload');
    primaryActions.appendChild(primaryGroup);
    shell.appendChild(primaryActions);

    const mainActions = document.createElement('div');
    mainActions.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2';
    const mainGroup = document.createElement('div');
    mainGroup.className = 'd-flex flex-wrap align-items-center gap-1';
    renderAdventureManagementActions(mainGroup, card);
    mainActions.appendChild(mainGroup);
    const response = document.createElement('div');
    response.id = `div_reponse_adventure_card_${card.item_id}`;
    mainActions.appendChild(response);
    shell.appendChild(mainActions);

    return col;
}

interface BuiltCard { el: HTMLElement; text: string; }

export function mountCatalogMyAdventures(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-my-adventures] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    // Optional per-mount empty message (public/private sections carry their own flavour text).
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No adventures found.';
    // Distinct copy for the "active search, nothing matched" case so each section names itself
    // ("No public adventures match…" / "No private adventures match…") instead of an ambiguous
    // shared line — the two stacked lists otherwise read as one and a no-match in the top section
    // looks like the whole search failed.
    const emptySearchText = root.getAttribute('data-ft-empty-search-text') || 'No adventures match your search.';

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            // Read as text first so a stray PHP warning / HTML 500 surfaces a useful message rather
            // than an opaque JSON.parse error (same guard as render-my-campaigns.ts).
            const raw = await res.text();
            let data: CatalogMyAdventuresResponse;
            try {
                data = JSON.parse(raw) as CatalogMyAdventuresResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            const errData = data as unknown as { error?: string; detail?: string };
            if (!res.ok || errData.error) {
                throw new Error(errData.detail || errData.error || `HTTP ${res.status}`);
            }
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(root, container, data, searchInput, emptyText, emptySearchText);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-my-adventures] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = 'Could not load adventures. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function build(
    root: HTMLElement,
    container: HTMLElement,
    data: CatalogMyAdventuresResponse,
    searchInput: HTMLInputElement | null,
    emptyText: string,
    emptySearchText: string,
): void {
    container.innerHTML = '';

    validateAdventureList(data, 'my');
    const built: BuiltCard[] = [];
    for (const row of data.rows) {
        const card = adventureMyCard(row);
        const el = renderMyAdventureCard(card);
        container.appendChild(el);
        built.push({ el, text: card.text || (card.name || '').toLowerCase() });
    }

    // Init Bootstrap tooltips on freshly-built icons (the management buttons embed iconMap() output
    // whose labels live in data-bs-original-title) — same belt-and-suspenders pass as my-campaigns.
    initTooltips(container);

    // Hover-play header videos: initializeVideoCards() (campaign-init.js) is a one-shot DOMContentLoaded
    // scan, so the cards we injected async need a re-scan to wire their autoplay (it skips .initialized,
    // so this only touches the new ones). Same hydrate pattern as the library renderer.
    const initVideos = (window as unknown as { initializeVideoCards?: () => void }).initializeVideoCards;
    if (typeof initVideos === 'function') initVideos();

    // Empty-state at the top of the reserved min-height space (shown when nothing matches, or the
    // section genuinely has no adventures).
    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = emptyText;
    emptyEl.style.display = built.length ? 'none' : '';
    container.appendChild(emptyEl);
    const emptySpec = built.length === 0 ? emptyStateFromAttributes(root, emptyText) : null;
    if (emptySpec) {
        emptyEl.className = 'col-12';
        emptyEl.replaceChildren(buildEmptyState(emptySpec));
    }

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        let visible = 0;
        for (const c of built) {
            const show = !keyword || c.text.includes(keyword);
            c.el.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        if (!emptySpec) emptyEl.textContent = keyword ? emptySearchText : emptyText;
        emptyEl.style.display = visible === 0 ? '' : 'none';
    };

    // Live keyword search + the house spyglass/clear trailing icons (same wiring as my-campaigns).
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    applyFilter();
}
